import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { MessageSender, MessageToApprove } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import MessageItem from './message-item';

interface Props {
  open: boolean;
  loading: boolean;
  messages: MessageToApprove[];
  messageIndex: number;
  messageSender: MessageSender | null;
  messageToEditText: string | null;
  messageToEditIndex: number | null;

  handleNextMessage(): void;

  handlePrevMessage(): void;

  handleRemoveContact(linkedInURL: string, idx: number): () => void;

  handleEditMessage(idx: number): () => void;

  handleCancelEditMessage(): void;

  handleMessageToApproveChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSaveAndApproveMessage(): void;

  handleApproveMessage(idx: number): () => void;

  handleOpenConfirmDeclineMessageDialog(): void;

  handleClose(): void;

  handleOpenContactDrawer(url: string, profileId: string, funnelId?: string): () => void;
}

const ApproveMessagesDialog: React.FC<Props> = ({
  open,
  loading,
  messages,
  messageIndex,
  messageSender,
  messageToEditText,
  messageToEditIndex,
  handleNextMessage,
  handlePrevMessage,
  handleRemoveContact,
  handleEditMessage,
  handleCancelEditMessage,
  handleMessageToApproveChange,
  handleSaveAndApproveMessage,
  handleApproveMessage,
  handleOpenConfirmDeclineMessageDialog,
  handleClose,
  handleOpenContactDrawer,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const message = messages?.[messageIndex];
  const isEditing = !_.isNil(messageToEditIndex) && messageIndex === messageToEditIndex;

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography className={classes.titleText}>{t('messagesToApprove')}</Typography>
          <IconButton className={classes.closeBtn} size={'small'} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className={classes.messageStepper}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <IconButton className={classes.iconButton} onClick={handlePrevMessage} disabled={loading}>
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography className={classes.greyText}>{`${t('message')} ${messageIndex + 1} / ${messages.length}`}</Typography>
          <IconButton className={classes.iconButton} onClick={handleNextMessage} disabled={loading}>
            <ArrowForwardRoundedIcon />
          </IconButton>
        </Stack>
      </DialogContent>

      <DialogContent className={classes.content}>
        {loading ? (
          <PaiDotLoader />
        ) : (
          <MessageItem
            message={message}
            sender={messageSender}
            messageToEditText={messageToEditText}
            isEditing={isEditing}
            handleEditMessage={handleEditMessage(messageIndex)}
            handleCancelEditMessage={handleCancelEditMessage}
            handleMessageToApproveChange={handleMessageToApproveChange}
            handleOpenContactDrawer={handleOpenContactDrawer}
          />
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {!isEditing ? (
          <>
            <PaiTooltip title={t('removeContactFromCampaignDescription')}>
              <span>
                <PaiLink
                  text={t('removeContactFromCampaign')}
                  size={'lg'}
                  variant={'default'}
                  underline={'always'}
                  disabled={loading}
                  action={!_.isNil(message?.linkedInURL) ? handleRemoveContact(message.linkedInURL, messageIndex) : undefined}
                />
              </span>
            </PaiTooltip>
            <PaiLink
              text={t('declineMessage')}
              size={'lg'}
              variant={'default'}
              underline={'always'}
              action={handleOpenConfirmDeclineMessageDialog}
              disabled={loading}
            />
            <PaiButton text={t('approveMessage')} size={'small'} border={'rounded'} action={handleApproveMessage(messageIndex)} loading={loading} />
          </>
        ) : (
          <>
            <PaiTooltip title={t('removeContactFromCampaignDescription')}>
              <span>
                <PaiLink
                  text={t('removeContactFromCampaign')}
                  size={'lg'}
                  variant={'default'}
                  underline={'always'}
                  disabled={loading}
                  action={!_.isNil(message?.linkedInURL) ? handleRemoveContact(message.linkedInURL, messageIndex) : undefined}
                />
              </span>
            </PaiTooltip>
            <PaiLink
              text={t('declineMessage')}
              size={'lg'}
              variant={'default'}
              underline={'always'}
              action={handleOpenConfirmDeclineMessageDialog}
              disabled={loading}
            />
            <PaiButton text={t('saveAndApprove')} size={'small'} border={'rounded'} action={handleSaveAndApproveMessage} loading={loading} />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  closeBtn: {
    color: '#FFF',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  messageStepper: {
    padding: '16px 32px !important',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  iconButton: {
    padding: '2px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  actions: {
    borderTop: `1px solid ${theme.palette.greys.light}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '20px 24px',
  },
}));

export default ApproveMessagesDialog;
